import { useState, useCallback } from "react"
import { X, Check, MoreHorizontal, Pencil, Sparkles, Plus, AlertTriangle } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Badge } from "@/components/ui/badge"
import { ThemeEvidenceBadge } from "@/components/strategy/ThemeEvidenceBadge"
import type { PendingThemeSuggestion } from "@/stores/strategy-store"
import { Textarea } from "@/components/ui/textarea"

interface PendingThemeCardProps {
  pending: PendingThemeSuggestion
  index: number
  onAccept: () => void
  onDismiss: () => void
  onEdit: () => void
  onRefine: (instruction: string) => Promise<void>
}

const CATEGORY_LABELS: Record<string, string> = {
  experience: 'Experience',
  personnel: 'Personnel',
  methodology: 'Methodology',
  innovation: 'Innovation',
  relationship: 'Relationship',
  value: 'Value',
  risk: 'Risk',
  local_presence: 'Local presence',
  other: 'Other',
}

const STRENGTH_VARIANT: Record<string, 'default' | 'outline' | 'secondary'> = {
  strong: 'default',
  moderate: 'secondary',
  weak: 'outline',
}

export function PendingThemeCard({ pending, index, onAccept, onDismiss, onEdit, onRefine }: PendingThemeCardProps) {
  const [refineOpen, setRefineOpen] = useState(false)
  const [instruction, setInstruction] = useState('')
  const [refining, setRefining] = useState(false)

  const handleRefineSubmit = useCallback(async () => {
    const trimmed = instruction.trim()
    if (!trimmed) return
    setRefining(true)
    try {
      await onRefine(trimmed)
      setInstruction('')
      setRefineOpen(false)
    } finally {
      setRefining(false)
    }
  }, [instruction, onRefine])

  return (
    // Each card has an Accept and a Dismiss waiting on the user, so it earns
    // the accent — but as a left edge, not a fill. The fill repeated once per
    // card, so five suggestions became five stacked azure bands and the third
    // one had nothing left to say. An edge repeats without accumulating.
    <div className="rounded-xl border border-border border-l-2 border-l-[hsl(var(--brand))] bg-card shadow-sm p-4 group">
      <div className="flex items-start justify-between mb-1 gap-2">
        <div className="flex items-center gap-2.5 flex-wrap">
          <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium leading-none bg-[hsl(var(--ai-accent-soft))] text-[hsl(var(--ai-accent-text))]">
            <Sparkles className="h-2.5 w-2.5" />
            Pending
          </span>
          <span className="text-xs font-semibold text-muted-foreground tabular-nums">{index + 1}</span>
          <h4 className="text-sm font-medium">{pending.title}</h4>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-7 w-7">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuItem onClick={onEdit}>
                <Pencil className="h-3.5 w-3.5 mr-2" />
                Edit theme
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setRefineOpen(true)}
                className="text-[hsl(var(--ai-accent-text))] focus:text-[hsl(var(--ai-accent-text))] focus:bg-[hsl(var(--ai-accent-soft))]"
              >
                <Sparkles className="h-3.5 w-3.5 mr-2" />
                Refine with AI
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground" onClick={onDismiss} aria-label="Dismiss">
            <X className="h-4 w-4" />
          </Button>
          <Button size="sm" className="h-8 px-2.5 text-xs" onClick={onAccept}>
            <Check className="h-3.5 w-3.5 mr-1" />
            Accept
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <span className="text-meta">{CATEGORY_LABELS[pending.category] ?? pending.category}</span>
        <span className="text-meta">·</span>
        <Badge variant={STRENGTH_VARIANT[pending.strength] ?? 'secondary'}>
          {pending.strength.charAt(0).toUpperCase() + pending.strength.slice(1)}
        </Badge>
        <ThemeEvidenceBadge strength={pending.evidenceStrength} count={pending.libraryEvidenceCount} />
      </div>

      {pending.description && (
        <p className="text-sm text-foreground leading-relaxed mb-2">{pending.description}</p>
      )}

      {pending.evidenceStrength === 'gap' && (
        <div className="rounded-lg p-3 bg-destructive/5 border border-destructive/20 mt-2">
          <div className="flex items-start gap-2 mb-1">
            <AlertTriangle className="h-3.5 w-3.5 text-destructive shrink-0 mt-0.5" />
            <div className="text-xs font-medium text-destructive">Evidence gap — action needed</div>
          </div>
          <div className="text-meta ml-5">
            No supporting case studies, certifications, or projects found in your library. Add evidence before submission, or accept without and revisit later.
          </div>
          <div className="mt-2 ml-5 flex gap-2">
            <Button variant="ghost" size="sm" className="h-7 px-2 text-[11px]">
              <Plus className="h-3 w-3 mr-1" />
              Add to library
            </Button>
            <Button variant="ghost" size="sm" className="h-7 px-2 text-[11px]" onClick={onAccept}>
              Accept without evidence
            </Button>
          </div>
        </div>
      )}

      {refineOpen && (
        <div className="mt-3 rounded-lg border border-border bg-card p-3">
          <div className="text-xs font-medium mb-2 flex items-center gap-1.5">
            <Sparkles className="h-3 w-3 text-[hsl(var(--ai-accent-text))]" />
            Refine with AI
          </div>
          <Textarea
            value={instruction}
            onChange={e => setInstruction(e.target.value)}
            placeholder="e.g. Shorter and more outcomes-focused. Tie to our recent Hobart project."
            className="text-xs resize-none bg-card border-border"
            rows={3}
            disabled={refining}
          />
          <div className="flex justify-end mt-2 gap-2">
            <Button variant="ghost" size="sm" onClick={() => { setRefineOpen(false); setInstruction('') }} disabled={refining}>
              Cancel
            </Button>
            <Button
              size="sm"
              className="text-xs"
              disabled={!instruction.trim() || refining}
              onClick={() => void handleRefineSubmit()}
            >
              <Sparkles className="h-3 w-3 mr-1.5" />
              {refining ? 'Refining…' : 'Refine'}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
